import { apiClient } from '@/hooks/useApi';
import { IntakePhoto } from './intakeApi';

export type WorkOrderStatus = 'Pending' | 'Booked' | 'InProgress' | 'AwaitingParts' | 'AwaitingApproval' | 'Ready' | 'Completed' | 'Cancelled' | 'OnHold' | 'Reopened';
export type WorkOrderPriority = 'Low' | 'Normal' | 'High' | 'Urgent';
export type WorkOrderItemType = 'Part' | 'Labor' | 'Service';

export interface WorkOrderItem {
  workOrderItemId?: number;
  itemType: WorkOrderItemType;
  description: string;
  quantity: number;
  unitPrice: number;
  laborHours?: number;
  laborRate?: number;
  discount: number;
  total?: number;
}

export interface WorkOrder {
  workOrderId: string;
  customerId: string;
  vehicleId: string;
  status: WorkOrderStatus;
  priority: WorkOrderPriority;
  description: string;
  assignedStaffId?: string;
  estimatedCost?: number;
  actualCost?: number;
  createdAt: string;
  completedAt?: string;
}

export interface WorkOrderBoardItem {
  workOrderId: number;
  workOrderNumber: string;
  status: string;
  priority: string;
  vehicleInfo: string;
  vehiclePlate: string;
  customerName: string;
  serviceDescription: string;
  assignedTechName?: string;
  assignedTechInitials?: string;
  createdDate: string;
  updatedDate: string;
}

export interface WorkOrderTask {
  workOrderTaskId: number;
  description: string;
  code?: string;
  price: number;
  isCompleted: boolean;
  estimatedMinutes?: number;
}

export interface ChatThreadItem {
  workOrderId: number;
  workOrderNumber: string;
  vehicleInfo: string;
  customerLastName: string;
  assignedTechnicianName?: string | null;
  lastMessagePreview: string;
  lastSenderName: string;
  lastMessageAt: string;
  unreadCount: number;
}

export interface WorkOrderSummary {
  workOrderId: number;
  workOrderNumber: string;
  createdDate: string;
  completedDate?: string | null;
  status: string;
  priority: string;
  customerName: string;
  vehicle: string;
  totalAmount: number;
}

export const workOrdersApi = {
  async list(status?: WorkOrderStatus): Promise<WorkOrderSummary[]> {
    const { data } = await apiClient.get<WorkOrderSummary[]>('/api/workorders', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async get(id: string): Promise<WorkOrder> {
    const { data } = await apiClient.get<WorkOrder>(`/api/workorders/${id}`);
    return data;
  },

  async getWorkOrderBoard(myJobs: boolean = false, includeArchived: boolean = false): Promise<WorkOrderBoardItem[]> {
    const { data } = await apiClient.get<WorkOrderBoardItem[]>('/api/workorders/board', {
      params: { myJobs, includeArchived },
    });
    return data;
  },

  async getArchivedWorkOrders(days: number = 30, myJobs: boolean = false): Promise<WorkOrderBoardItem[]> {
    const { data } = await apiClient.get<WorkOrderBoardItem[]>('/api/workorders/board/archived', {
      params: { days, myJobs },
    });
    return data;
  },

  async completeFromBay(id: number, notes?: string): Promise<{ success: boolean; message: string }> {
    const { data } = await apiClient.post<{ success: boolean; message: string }>(
      `/api/workorders/${id}/complete-from-bay`,
      { notes }
    );
    return data;
  },

  async create(payload: Omit<WorkOrder, 'workOrderId' | 'createdAt'>): Promise<WorkOrder> {
    const { data } = await apiClient.post<WorkOrder>('/api/workorders', payload);
    return data;
  },

  async update(id: string, payload: Partial<WorkOrder> & { items?: Omit<WorkOrderItem, 'workOrderItemId'>[] }): Promise<WorkOrder> {
    const { data } = await apiClient.put<WorkOrder>(`/api/workorders/${id}`, payload);
    return data;
  },

  async updateStatus(id: number, status: string, queueSms: boolean = false): Promise<void> {
    await apiClient.post(`/api/workorders/${id}/status`, { status, queueSms });
  },

  async updateWorkOrderStatus(id: number, status: string): Promise<void> {
    await apiClient.post(`/api/workorders/${id}/status`, { status });
  },

  async getTasks(id: number): Promise<WorkOrderTask[]> {
    const { data } = await apiClient.get<WorkOrderTask[]>(`/api/workorders/${id}/tasks`);
    return data;
  },

  async updateTaskCompletion(id: number, taskId: number, isCompleted: boolean): Promise<void> {
    await apiClient.patch(`/api/workorders/${id}/tasks/${taskId}`, { isCompleted });
  },

  async updateNotes(id: number, technicianNotes: string): Promise<void> {
    await apiClient.patch(`/api/workorders/${id}/notes`, { technicianNotes });
  },

  async updatePriority(id: number, priority: string): Promise<void> {
    await apiClient.patch(`/api/workorders/${id}/priority`, { priority });
  },

  // Chat endpoints
  async getWorkOrderChat(id: number): Promise<any[]> {
    const { data } = await apiClient.get(`/api/workorders/${id}/chat`);
    return data;
  },

  async sendWorkOrderChatMessage(id: number, payload: { messageText: string }): Promise<any> {
    const { data } = await apiClient.post(`/api/workorders/${id}/chat`, payload);
    return data;
  },

  async getUnreadChatCount(): Promise<{ totalUnreadMessages: number; affectedWorkOrders: number }> {
    const { data } = await apiClient.get('/api/chat/unread-count');
    return data;
  },

  // Job detail endpoint
  async getWorkOrderDetail(id: number): Promise<any> {
    const { data } = await apiClient.get(`/api/workorders/${id}`);
    return data;
  },

  async updateWorkOrderTask(workOrderId: number, taskId: number, isCompleted: boolean): Promise<void> {
    await apiClient.patch(`/api/workorders/${workOrderId}/tasks/${taskId}`, { isCompleted });
  },

  async getChatThreads(): Promise<ChatThreadItem[]> {
    const { data } = await apiClient.get<ChatThreadItem[]>('/api/chat/threads');
    return data;
  },

  async markAllChatRead(workOrderId: number): Promise<void> {
    await apiClient.patch(`/api/workorders/${workOrderId}/chat/mark-all-read`);
  },

  async assignTechnician(id: number, technicianId: number | null): Promise<void> {
    await apiClient.patch(`/api/workorders/${id}/assign`, { technicianId });
  },

  async cancelWorkOrder(id: number, reason: string): Promise<void> {
    await apiClient.post(`/api/workorders/${id}/cancel`, { reason });
  },

  async updateStatusWithReason(id: number, status: string, reason?: string, queueSms?: boolean): Promise<void> {
    await apiClient.post(`/api/workorders/${id}/status`, { status, reason, queueSms: queueSms ?? false });
  },

  async sendSms(id: number, message: string): Promise<{ queued: boolean; phone: string }> {
    const { data } = await apiClient.post<{ queued: boolean; phone: string }>(`/api/workorders/${id}/sms`, { message });
    return data;
  },

  // Photos carried over from the intake this work order was converted from (if any).
  async getPhotos(id: number): Promise<IntakePhoto[]> {
    const { data } = await apiClient.get<IntakePhoto[]>(`/api/workorders/${id}/photos`);
    return Promise.all(data.map(async (photo) => {
      try {
        const response = await apiClient.get<Blob>(`/api/workorders/${id}/photos/${photo.photoId}/content`, { responseType: 'blob' });
        return { ...photo, url: URL.createObjectURL(response.data) };
      } catch { return photo; }
    }));
  },

  async deletePhoto(workOrderId: number, photoId: number): Promise<void> {
    await apiClient.delete(`/api/workorders/${workOrderId}/photos/${photoId}`);
  },
};
